import { useState } from "react";
import { Link, useLocation } from "wouter";
import { Droplets, Mail, Lock, User, Loader2, Phone, Building2, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useToast } from "@/hooks/use-toast";
import * as firestore from "@/lib/firestore";
import { CompanyStatus, CorporatePaymentType } from "@shared/schema";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { auth } from "@/lib/firebase";
import { normalizePhoneNumber, isValidTurkishPhone } from "@/lib/utils";
import { trackSignUp } from "@/lib/analytics";

export default function Register() {
  const [, setLocation] = useLocation();
  const { signIn } = useAuth();
  const { toast } = useToast();
  
  // Basic fields
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [loading, setLoading] = useState(false);

  // Customer type
  const [customerType, setCustomerType] = useState<"individual" | "corporate">("individual");

  // Corporate fields
  const [companyName, setCompanyName] = useState("");
  const [taxNumber, setTaxNumber] = useState("");
  const [wantsInvoice, setWantsInvoice] = useState(false);

  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (password !== confirmPassword) {
      toast({
        title: "Hata",
        description: "Şifreler eşleşmiyor",
        variant: "destructive",
      });
      return;
    }

    if (password.length < 6) {
      toast({
        title: "Hata",
        description: "Şifre en az 6 karakter olmalıdır",
        variant: "destructive",
      });
      return;
    }

    if (!phoneNumber) {
      toast({
        title: "Hata",
        description: "Telefon numarası zorunludur",
        variant: "destructive",
      });
      return;
    }

    // Normalize and validate phone number
    const normalizedPhone = normalizePhoneNumber(phoneNumber);
    
    if (!normalizedPhone || !isValidTurkishPhone(phoneNumber)) {
      toast({
        title: "Geçersiz Telefon Numarası",
        description: "Lütfen geçerli bir Türkiye telefon numarası girin. Örnek formatlar: 05334580556, 533 458 05 56, +905334580556",
        variant: "destructive",
      });
      return;
    }

    // Phone verification removed - will be done during booking

    // Corporate validation
    if (customerType === "corporate") {
      if (!companyName) {
        toast({
          title: "Hata",
          description: "Şirket adı zorunludur",
          variant: "destructive",
        });
        return;
      }

      if (wantsInvoice && !taxNumber) {
        toast({
          title: "Hata",
          description: "Fatura için vergi numarası zorunludur",
          variant: "destructive",
        });
        return;
      }
    }

    setLoading(true);
    let createdUser: any = null;
    
    try {
      // Create Firebase user
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const userId = userCredential.user.uid;
      createdUser = userCredential.user; // Store for rollback

      let companyId: string | null = null;

      // If corporate, create company
      if (customerType === "corporate") {
        const newCompany = await firestore.createCorporateCompany({
          companyName: companyName,
          taxNumber: wantsInvoice ? taxNumber : "",
          industry: "",
          address: "",
          city: "",
          postalCode: "",
          contactName: displayName,
          contactEmail: email,
          contactPhone: normalizedPhone,
          contactTitle: "Yetkili",
          notes: "Kayıt sırasında oluşturuldu",
          status: CompanyStatus.LEAD, // Başlangıç: Potansiyel müşteri
          paymentType: CorporatePaymentType.CARI_HESAP, // Default: Cari hesap
          authorizedEmails: [email], // Add the registering user's email
          authorizedUserIds: [userId], // Add the registering user's ID
        });

        companyId = newCompany.id;
      }

      // Create user document in Firestore
      await firestore.createUser(userId, {
        email: email,
        displayName: displayName,
        phone: normalizedPhone,
        phoneVerified: false, // Will be verified during first booking
        companyId: companyId,
        role: "customer",
        photoURL: "",
      });

      // Track successful sign up
      trackSignUp('email', customerType);

      toast({
        title: "Başarılı",
        description: customerType === "corporate" 
          ? "Kurumsal hesabınız oluşturuldu! Yönlendiriliyorsunuz..." 
          : "Hesabınız oluşturuldu! Yönlendiriliyorsunuz...",
      });

      setTimeout(() => {
        setLocation("/customer");
      }, 1000);
    } catch (error: any) {
      
      // ROLLBACK: Delete Firebase Auth user if Firestore operations failed
      if (createdUser && !error.code?.startsWith("auth/")) {
        try {
          await createdUser.delete();
        } catch (deleteError) {
          // User exists in Firebase Auth but not in Firestore - admin needs to clean up
        }
      }
      
      let errorMessage = "Kayıt olurken bir hata oluştu";
      
      if (error.code === "auth/email-already-in-use") {
        errorMessage = "Bu e-posta adresi zaten kullanılıyor";
      } else if (error.code === "auth/invalid-email") {
        errorMessage = "Geçersiz e-posta adresi";
      } else if (error.code === "auth/weak-password") {
        errorMessage = "Şifre çok zayıf";
      } else if (error.message) {
        // Show detailed error for debugging
        errorMessage = `Kayıt hatası: ${error.message}`;
      }
      
      toast({
        title: "Hata",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      // This will redirect to Google - page will reload after authentication
      await signIn();
      
      toast({
        title: "Yönlendiriliyor",
        description: "Google hesabınızla giriş yapılıyor...",
      });
      // Note: Page redirects to Google, AuthContext handles the result
    } catch (error) {
      toast({
        title: "Hata",
        description: "Google ile giriş yapılırken bir hata oluştu",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Navigation */}
      <nav className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link href="/">
              <div className="flex items-center gap-2 cursor-pointer hover-elevate active-elevate-2 px-3 py-2 rounded-lg transition-all" data-testid="link-home">
                <Droplets className="w-8 h-8 text-primary" />
                <span className="text-xl font-bold text-primary">TemizFilo</span>
              </div>
            </Link>
            <ThemeToggle />
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md p-8 rounded-2xl">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
              <Droplets className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-3xl font-bold mb-2 text-primary">🫧 Kayıt Ol 🫧</h1>
            <p className="text-muted-foreground">
              Yeni hesap oluştur ve hizmetlerimizden yararlan
            </p>
          </div>

          <form onSubmit={handleEmailSignUp} className="space-y-4">
            {/* Customer Type Selection */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Müşteri Tipi</Label>
              <RadioGroup
                value={customerType}
                onValueChange={(value) => {
                  setCustomerType(value as "individual" | "corporate");
                  if (value === "corporate") {
                    setWantsInvoice(true); // Kurumsal müşteriler için fatura varsayılan
                  }
                }}
                className="grid grid-cols-2 gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="individual" id="individual" data-testid="radio-individual" />
                  <Label htmlFor="individual" className="cursor-pointer font-normal">
                    <User className="w-4 h-4 inline mr-1" />
                    Bireysel
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="corporate" id="corporate" data-testid="radio-corporate" />
                  <Label htmlFor="corporate" className="cursor-pointer font-normal">
                    <Building2 className="w-4 h-4 inline mr-1" />
                    Kurumsal
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Basic Information */}
            <div className="space-y-2">
              <Label htmlFor="displayName">{customerType === "corporate" ? "Yetkili Adı Soyadı" : "Ad Soyad"}</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="displayName"
                  type="text"
                  placeholder={customerType === "corporate" ? "İrtibat Kişisi Adı Soyadı" : "Adınız Soyadınız"}
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="pl-10"
                  required
                  data-testid="input-displayname"
                />
              </div>
            </div>

            {/* Corporate Fields */}
            {customerType === "corporate" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="companyName">Şirket Adı</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="companyName"
                      type="text"
                      placeholder="Şirket Ünvanı"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="input-company-name"
                    />
                  </div>
                </div>

                <div className="space-y-3 p-4 bg-muted/50 rounded-lg border border-border">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="wantsInvoice"
                      checked={wantsInvoice}
                      onCheckedChange={(checked) => setWantsInvoice(checked as boolean)}
                      data-testid="checkbox-wants-invoice"
                    />
                    <Label htmlFor="wantsInvoice" className="cursor-pointer font-medium">
                      <FileText className="w-4 h-4 inline mr-1" />
                      Fatura İstiyorum
                    </Label>
                  </div>

                  {wantsInvoice && (
                    <div className="space-y-2 mt-3">
                      <Label htmlFor="taxNumber">Vergi Numarası</Label>
                      <Input
                        id="taxNumber"
                        type="text"
                        placeholder="10 haneli vergi numarası"
                        value={taxNumber}
                        onChange={(e) => setTaxNumber(e.target.value)}
                        maxLength={10}
                        required
                        data-testid="input-tax-number"
                      />
                      <p className="text-xs text-muted-foreground">
                        Fatura için vergi numaranızı girmeniz gerekmektedir
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="phoneNumber">Telefon Numarası</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="phoneNumber"
                  type="tel"
                  placeholder="05XX XXX XX XX"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="pl-10"
                  required
                  data-testid="input-phone"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">E-posta</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="ornek@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                  data-testid="input-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Şifre</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  required
                  minLength={6}
                  data-testid="input-password"
                />
              </div>
              <p className="text-xs text-muted-foreground">En az 6 karakter</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Şifre Tekrar</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10"
                  required
                  minLength={6}
                  data-testid="input-confirm-password"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
              data-testid="button-register"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {customerType === "corporate" ? "Kurumsal Hesap Oluşturuluyor..." : "Kayıt Olunuyor..."}
                </>
              ) : (
                customerType === "corporate" ? "Kurumsal Hesap Oluştur" : "Kayıt Ol"
              )}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-card text-muted-foreground">veya</span>
            </div>
          </div>

          <Button
            variant="outline"
            className="w-full"
            onClick={handleGoogleSignIn}
            data-testid="button-google-signup"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Google ile Kayıt Ol
          </Button>

          <div className="mt-6 text-center text-sm">
            <span className="text-muted-foreground">Zaten hesabınız var mı? </span>
            <Link href="/login">
              <a className="text-primary font-semibold hover:underline" data-testid="link-login">
                Giriş Yap
              </a>
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
